import { supabase } from "@/integrations/supabase/client";
import { SubscriptionTier } from "../subscriptions/types";

export interface UserSubscriptionDetails {
  subscription: {
    id: string;
    user_id: string;
    tier: SubscriptionTier;
    status: string;
    current_period_start: string | null;
    current_period_end: string | null;
    created_at: string;
    updated_at: string;
  } | null;
  lead_count: number;
  lead_imports: number;
}

export interface SubscriptionChangeLog {
  id: string;
  subscription_id: string;
  user_id: string;
  changed_by: string;
  old_tier: SubscriptionTier;
  new_tier: SubscriptionTier;
  old_status: string | null;
  new_status: string | null;
  old_current_period_end: string | null;
  new_current_period_end: string | null;
  reason: string;
  created_at: string;
}

export const getAllUserSubscriptions = async () => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select(`
      *,
      profiles!inner(id, first_name, last_name, email)
    `)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data;
};

export const getUserSubscriptionDetails = async (
  userId: string
): Promise<UserSubscriptionDetails | null> => {
  const { data, error } = await supabase.rpc('admin_get_user_subscription', {
    p_user_id: userId
  });

  if (error) {
    console.error('Error fetching user subscription:', error);
    throw error;
  }

  return data as unknown as UserSubscriptionDetails | null;
};

export const assignSubscription = async (
  userId: string,
  tier: SubscriptionTier,
  reason: string,
  durationDays: number = 30
) => {
  const { data, error } = await supabase.rpc('admin_assign_subscription', {
    p_user_id: userId,
    p_tier: tier,
    p_reason: reason,
    p_duration_days: durationDays
  });

  if (error) {
    console.error('Error assigning subscription:', error);
    throw error;
  }

  return data;
};

export const getSubscriptionChangeHistory = async (userId: string) => {
  const { data, error } = await supabase
    .from('subscription_change_logs')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching change history:', error);
    throw error;
  }

  // Fetch admin profiles separately
  if (data && data.length > 0) {
    const adminIds = [...new Set(data.map(log => log.changed_by))];
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, email')
      .in('id', adminIds);

    const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

    return data.map(log => ({
      ...log,
      changed_by_profile: profileMap.get(log.changed_by) || {
        first_name: null,
        last_name: null,
        email: null
      }
    })) as (SubscriptionChangeLog & {
      changed_by_profile: {
        first_name: string | null;
        last_name: string | null;
        email: string | null;
      };
    })[];
  }

  return [];
};
